import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { STANDARD_QUESTION_COUNT } from '../utils/constants';

const TARGET_SCOPE_TYPES = ['organization', 'site', 'department', 'team'];

/** `initial` = an existing draft to edit (Tim 26 Oct, D05 r1 / Q-11): fields prefill, Save keeps it a draft. */
function CreateSurveyDialog({ open, onClose, onSave, organization, initial = null }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetScope, setTargetScope] = useState('organization:all');
  const [dueDate, setDueDate] = useState(null);
  const [questionSet, setQuestionSet] = useState('standard');
  const [anonymous, setAnonymous] = useState(false);
  const [targetResponses, setTargetResponses] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const sites = organization?.sites || [];
  const departments = organization?.departments || [];
  const teams = organization?.teams || [];

  const handleScopeChange = (e) => {
    setTargetScope(e.target.value);
  };

  const parseScope = (scopeValue) => {
    const [type, id] = scopeValue.split(':');
    return {
      type,
      id: id === 'all' ? (organization?.id || null) : id,
    };
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTargetScope('organization:all');
    setDueDate(null);
    setQuestionSet('standard');
    setAnonymous(false);
    setTargetResponses('');
    setError(null);
    setSaving(false);
  };

  // Prefill from an existing draft when editing
  useEffect(() => {
    if (!open) return;
    if (!initial) { resetForm(); return; }
    setTitle(initial.title || '');
    setDescription(initial.description || '');
    const sc = initial.targetScope;
    setTargetScope(sc && sc.type && sc.type !== 'organization' ? `${sc.type}:${sc.id}` : 'organization:all');
    setDueDate(initial.dueDate ? dayjs(initial.dueDate) : null);
    setQuestionSet(initial.questionSet || 'standard');
    setAnonymous(Boolean(initial.anonymous));
    setTargetResponses(initial.targetResponses ? String(initial.targetResponses) : '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async (saveAsStatus) => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const surveyData = {
        title: title.trim(),
        description: description.trim(),
        status: saveAsStatus,
        targetScope: parseScope(targetScope),
        dueDate: dueDate ? dueDate.format('YYYY-MM-DD') : null,
        questionSet,
        anonymous,
        targetResponses: targetResponses ? parseInt(targetResponses, 10) : null,
      };
      await onSave(surveyData);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create survey');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Draft Survey' : 'Create New Survey'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Survey Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            autoFocus
            placeholder="e.g., Q1 2026 D&A Assessment"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description of the survey purpose"
          />

          <FormControl fullWidth>
            <InputLabel>Target Scope</InputLabel>
            <Select value={targetScope} onChange={handleScopeChange} label="Target Scope">
              <MenuItem value="organization:all">
                {organization?.name || 'Organization'} (Entire Org)
              </MenuItem>

              {/* Tim 26 Oct (D05 r1): grouped by site, each department and NWT named with its site —
                  "Operations" appeared ten times with no way to tell Houston from Dublin. */}
              {sites.map((site) => {
                const depts = departments.filter((d) => d.siteId === site.id);
                return [
                  <ListSubheader key={`h-${site.id}`}>{site.name}</ListSubheader>,
                  <MenuItem key={`site:${site.id}`} value={`site:${site.id}`}>
                    {site.name} — whole site
                  </MenuItem>,
                  ...depts.flatMap((dept) => [
                    <MenuItem key={`department:${dept.id}`} value={`department:${dept.id}`} sx={{ pl: 4 }}>
                      {dept.name} — {site.name}
                    </MenuItem>,
                    ...teams.filter((t) => t.departmentId === dept.id).map((team) => (
                      <MenuItem key={`team:${team.id}`} value={`team:${team.id}`} sx={{ pl: 6 }}>
                        ↳ {team.name} (NWT) — {dept.name}, {site.name}
                      </MenuItem>
                    )),
                  ]),
                ];
              })}
              {departments.filter((d) => !sites.some((s) => s.id === d.siteId)).length > 0 && <ListSubheader>Other departments</ListSubheader>}
              {departments.filter((d) => !sites.some((s) => s.id === d.siteId)).map((dept) => (
                <MenuItem key={`department:${dept.id}`} value={`department:${dept.id}`}>
                  {dept.name}
                </MenuItem>
              ))}
              {teams.filter((t) => !departments.some((d) => d.id === t.departmentId)).length > 0 && <ListSubheader>Other NWTs</ListSubheader>}
              {teams.filter((t) => !departments.some((d) => d.id === t.departmentId)).map((team) => (
                <MenuItem key={`team:${team.id}`} value={`team:${team.id}`}>
                  {team.name} (NWT)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Optional - when respondents should complete the survey by',
                },
              }}
              minDate={dayjs()}
            />
          </LocalizationProvider>

          <FormControl fullWidth>
            <InputLabel>Question Set</InputLabel>
            <Select value={questionSet} onChange={(e) => setQuestionSet(e.target.value)} label="Question Set">
              <MenuItem value="standard">Standard ({STANDARD_QUESTION_COUNT}-question D&A instrument)</MenuItem>
              <MenuItem value="custom" disabled>
                Custom (coming soon)
              </MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
            }
            label="Anonymous responses"
          />
          {anonymous && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5, ml: 4 }}>
              Respondent identities will not be stored with answers.
            </Typography>
          )}

          <TextField
            label="Expected Responses"
            value={targetResponses}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) > 0)) {
                setTargetResponses(val);
              }
            }}
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            placeholder="e.g., 50"
            helperText="Optional - number of expected respondents for progress tracking"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => handleSave('draft')}
          variant="outlined"
          disabled={saving}
        >
          {initial ? 'Save changes' : 'Save as Draft'}
        </Button>
        <Button
          onClick={() => handleSave('active')}
          variant="contained"
          disabled={saving}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateSurveyDialog;
